import { formatUnsignedMoney } from '@shared/utils'
import type {
  PortfolioComposition,
  PortfolioGraphRental,
  PortfolioGraphValue,
  PortfolioOverview,
} from '../types/portfolioTypes'

const TRAILING_ZERO_DECIMAL = '.0'
const MAX_API_PERCENT_DECIMALS = 3

/**
 * A percentage exactly as the API sends it, unsigned: 2.07 → "2.07%",
 * 6.5 → "6.5%", 2 → "2%". Only a value with MORE than 3 decimals is rounded
 * (to 3): 2.07519 → "2.075%" — returns are business figures, so they are
 * never re-rounded below the precision the backend chose.
 */
export const formatApiPercent = (value: number): string =>
  `${parseFloat(Math.abs(value).toFixed(MAX_API_PERCENT_DECIMALS))}%`

/** One decimal, no trailing `.0`, unsigned — `38.3%`, `40%`. Direction is drawn by an arrow. */
export const formatCompactPercent = (value: number): string => {
  const oneDp = Math.abs(value).toFixed(1)
  const trimmed = oneDp.endsWith(TRAILING_ZERO_DECIMAL)
    ? oneDp.slice(0, -TRAILING_ZERO_DECIMAL.length)
    : oneDp
  return `${trimmed}%`
}

/**
 * The overview endpoint reports valuation and rent separately and never adds
 * them, so the headline is assembled here. Every part comes from that one
 * payload — mixing in the holdings summary's total (scoped differently) could
 * leave the three segments not summing to the figure printed above them.
 */
export function buildPortfolioComposition(data: PortfolioOverview): PortfolioComposition {
  const currency = data.totalInvested.currency
  const invested = data.totalInvested.value
  const current = data.currentPortfolioValue.value
  const rent = data.totalRentalIncome.value

  const appreciation = current - invested
  const totalReturn = appreciation + rent
  const returnPct = invested > 0 ? (totalReturn / invested) * 100 : 0

  return {
    totalValue: formatUnsignedMoney(currency, current + rent),
    totalReturnPercent: `${totalReturn >= 0 ? '+' : '-'}${formatCompactPercent(returnPct)}`,
    isPositive: totalReturn >= 0,
    invested: formatUnsignedMoney(currency, invested),
    appreciation: formatUnsignedMoney(currency, Math.abs(appreciation)),
    isAppreciationPositive: appreciation >= 0,
    rentalIncome: formatUnsignedMoney(currency, rent),
    investedWeight: Math.max(invested, 0),
    appreciationWeight: Math.max(appreciation, 0),
    rentalWeight: Math.max(rent, 0),
  }
}

/**
 * Folds the rent already paid into the value graph's line, as investor-app does.
 * The graph plots valuation only, while the headline is valuation + rent — left
 * apart, the card and the chart's last point state two different portfolios.
 *
 * Anchored at the far end: the last month carries the all-time rent total and
 * each earlier month drops the rent paid after it (the rent series covers only
 * the fiscal-year window). Without the rent series the valuation is returned
 * unchanged rather than guessed at; unreported (null) months stay null.
 */
export function foldRentIntoValueGraph(
  value: PortfolioGraphValue | null | undefined,
  rental: PortfolioGraphRental | null | undefined,
  totalRentalIncome: number,
): PortfolioGraphValue | null | undefined {
  if (!value || !rental) return value

  const rentByMonth = new Map(rental.series.map((point) => [point.month, point.rentalIncome ?? 0]))

  const rentToDate: number[] = new Array(value.series.length)
  let paidAfter = 0
  for (let i = value.series.length - 1; i >= 0; i--) {
    // Clamped: a correction posted against an earlier year must not push a month negative.
    rentToDate[i] = Math.max(totalRentalIncome - paidAfter, 0)
    paidAfter += rentByMonth.get(value.series[i]?.month ?? '') ?? 0
  }

  return {
    ...value,
    series: value.series.map((point, i) => ({
      ...point,
      portfolioValue:
        point.portfolioValue === null ? null : point.portfolioValue + (rentToDate[i] ?? 0),
    })),
  }
}
